import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import type { PhotoGallery } from '@vibesites/schema';
import {
  useCollectionBinding,
  useProperty,
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

// Pulls an interior-leaning shot from the property photo gallery. Index 1 is
// the first non-hero photo, which in a typical gallery ordering is an interior
// or amenity shot (0 = hero exterior). suggestedFields gives the editor other
// gallery indices to try; if the property has fewer photos, the wrapper falls
// through to a plain surface tile.
const featureImageDef: SlotGroupDefinition = {
  label: 'Resident Review Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
      suggestedFields: [
        'photo_gallery[1].image',
        'photo_gallery[2].image',
        'photo_gallery[3].image',
        'photo_gallery[0].image',
        'property_featured_image',
      ],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: '',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Review collection item shape
// ============================================================================

interface ResidentReviewItem {
  name?: string | null;
  title?: string | null;
  text?: string | null;
  rating?: string | number | null;
  date?: string | null;
}

const REVIEW_LIMIT = 5;

/** Hard cap on the quote body so the blue card doesn't resize between slides. */
const MAX_REVIEW_CHARS = 260;

/**
 * Duration of the quote-text and feature-image crossfades. Both are driven
 * by the same `active` state change and use the same two-slot pattern, so
 * they animate in lockstep for exactly this many milliseconds.
 */
const FADE_MS = 500;

/** Strip lightweight HTML tags a richtext review body might carry. */
function stripTags(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

/**
 * Format a resident's name so only the first initial of the last name is
 * shown ("John Smith" → "John S."). Fair Housing–adjacent nuances aside, the
 * intent is a lightweight privacy nod for public-facing testimonials.
 *
 * - A single name is returned as-is (no trailing initial).
 * - Multiple tokens collapse to `<first> <initial-of-last>.` — any middle
 *   tokens are dropped.
 * - If the last token is already a single character or ends with a period
 *   (e.g. an existing "S." initial), the name is treated as pre-formatted
 *   and returned unchanged.
 */
function formatReviewerName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? '';
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (last.length === 1 || last.endsWith('.')) return `${first} ${last}`;
  const initial = last.charAt(0).toUpperCase();
  return `${first} ${initial}.`;
}

/** Truncate at a word boundary near `max` chars; append a single ellipsis. */
function truncateAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  const clipped = text.slice(0, max);
  const lastSpace = clipped.lastIndexOf(' ');
  const cut = lastSpace > max * 0.6 ? clipped.slice(0, lastSpace) : clipped;
  return cut.replace(/[\s.,;:!?—-]+$/, '') + '…';
}

/**
 * Best-effort Fair Housing Act compliance filter. Reviews that reference a
 * federally-protected class (race, color, religion, national origin, sex,
 * disability, familial status) — or a commonly state-extended class (age,
 * sexual orientation, gender identity, source of income, veteran status) —
 * are excluded because using them in marketing copy can imply an unlawful
 * preference. This is defensive filtering, not legal review.
 */
const FAIR_HOUSING_TERMS: string[] = [
  // Race / color / ethnicity
  'race', 'racial', 'racism', 'racist', 'ethnic', 'ethnicity',
  'asian', 'latino', 'latina', 'latinx', 'hispanic', 'african',
  'african-american', 'caucasian',
  // Religion
  'christian', 'christianity', 'catholic', 'protestant', 'muslim', 'islam',
  'jewish', 'jew', 'hindu', 'buddhist', 'atheist', 'religious', 'religion',
  'church', 'mosque', 'synagogue', 'temple', 'faith',
  // National origin
  'immigrant', 'immigration', 'foreigner', 'foreign', 'nationality',
  'indian', 'chinese', 'mexican', 'japanese', 'korean', 'russian', 'arab',
  // Sex / gender / sexual orientation / gender identity
  'gender', 'male', 'female', 'gay', 'lesbian', 'lgbt', 'lgbtq',
  'trans', 'transgender', 'queer', 'homosexual', 'heterosexual', 'bisexual',
  // Disability
  'disabled', 'disability', 'handicap', 'handicapped', 'wheelchair',
  'blind', 'deaf', 'crippled', 'impaired', 'autistic', 'autism', 'mentally',
  // Familial status
  'kid', 'kids', 'child', 'children', 'family', 'families',
  'family-friendly', 'baby', 'babies', 'toddler',
  'mom', 'mommy', 'dad', 'daddy', 'parent', 'parents', 'mother', 'father',
  'married', 'marriage', 'pregnant', 'pregnancy',
  'spouse', 'husband', 'wife',
  // Age
  'senior', 'seniors', 'elderly', 'retiree', 'retired', 'youth', 'teenager',
  // Source of income / veteran status
  'section 8', 'voucher', 'welfare', 'veteran', 'veterans', 'military',
];

const FAIR_HOUSING_REGEX = new RegExp(
  '\\b(?:' +
    FAIR_HOUSING_TERMS.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') +
    ')\\b',
  'i',
);

function isFairHousingCompliant(text: string): boolean {
  return !FAIR_HOUSING_REGEX.test(text);
}

/**
 * Property photo galleries occasionally include the property/brand logo as a
 * gallery item — great for the media library, terrible as a lifestyle hero.
 * A gallery photo is treated as a logo when the word "logo" appears in its
 * title, alt text, or description.
 */
const LOGO_REGEX = /\blogos?\b/i;

function isLogoPhoto(photo: PhotoGallery | undefined | null): boolean {
  if (!photo) return false;
  const text = `${photo.title ?? ''} ${photo.alt_text ?? ''} ${photo.description ?? ''}`;
  return LOGO_REGEX.test(text);
}

// ============================================================================
// Component
// ============================================================================

export function ResidentReview() {
  const featureImage = useSlotGroup('residentreview.image', featureImageDef);
  const property = useProperty();

  // Find the first gallery photo that (a) has an image and (b) isn't the
  // property logo. Used both as an outright fallback when nothing is bound and
  // as a rescue when the resolved image happens to be the logo item.
  const firstNonLogoPhoto = useMemo<PhotoGallery | undefined>(() => {
    const gallery = property?.photo_gallery ?? [];
    return gallery.find((p) => !!p?.image && !isLogoPhoto(p));
  }, [property]);

  // Detect the "bound image happens to be a logo" case — if the resolved URL
  // matches a gallery item flagged as a logo, override with the first non-logo
  // photo. Operator-picked overrides that point at real content pass through.
  const boundIsLogo = useMemo(() => {
    if (!featureImage.image) return false;
    const gallery = property?.photo_gallery ?? [];
    return gallery.some(
      (p) => (p.image === featureImage.image || p.original_url === featureImage.image) && isLogoPhoto(p),
    );
  }, [featureImage.image, property]);

  // Primary image (slide 0): honors the operator override from the slot
  // group, with the existing logo-rescue fallback. This is the same
  // resolution logic as before — extracted so we can build a per-slide list
  // on top of it.
  const primaryImage = boundIsLogo
    ? firstNonLogoPhoto?.image ?? ''
    : featureImage.image || firstNonLogoPhoto?.image || '';
  const primaryAlt = boundIsLogo
    ? firstNonLogoPhoto?.alt_text ?? ''
    : featureImage.alt || firstNonLogoPhoto?.alt_text || '';

  // Per-slide image list: primary first, then every other non-logo photo in
  // the gallery. We drop any gallery entry matching the primary URL so the
  // second slide doesn't repeat what the visitor just saw. When there are
  // more reviews than photos we cycle back around (see `currentSlide`).
  const slideImages = useMemo(() => {
    const gallery = property?.photo_gallery ?? [];
    const extras = gallery
      .filter((p) => !!p?.image && !isLogoPhoto(p) && p.image !== primaryImage)
      .map((p) => ({ image: p.image ?? '', alt: p.alt_text ?? '' }));
    return [{ image: primaryImage, alt: primaryAlt }, ...extras];
  }, [property, primaryImage, primaryAlt]);

  const rawReviews = useCollectionBinding<ResidentReviewItem>('residentreview.items', {
    collection: 'reviews',
    defaultLimit: REVIEW_LIMIT,
  });

  const reviews = useMemo(() => {
    const items = (rawReviews ?? [])
      .map((r) => ({ ...r, _plain: hasValue(r.text) ? stripTags(r.text) : '' }))
      .filter((r) => r._plain.length > 0)
      .filter((r) => isFairHousingCompliant(r._plain))
      .filter((r) => !hasValue(r.name) || isFairHousingCompliant(r.name!));
    return items.slice(0, REVIEW_LIMIT);
  }, [rawReviews]);

  const slideCount = reviews.length || 1;
  // `active` = which slide is currently selected (either by the visitor
  // clicking a dot or by auto-rotation). Both the quote text and the feature
  // image use two-slot crossfades that trigger off changes to this — so the
  // text and photo animate in perfect lockstep for the same duration.
  const [active, setActive] = useState(0);
  const safeActive = Math.min(active, slideCount - 1);
  const current = reviews[safeActive];

  // Quote text + author for the active review. Falls back to a hand-written
  // sample when the reviews collection is empty so the section still reads
  // sensibly in previews / new-property states.
  const fallbackText =
    'I love living here! It is so beautiful and cozy. The people are so sweet and make the place feel like home. I was so happy to interact with the office manager, she is an absolute gem.';
  const rawText = current?._plain || fallbackText;
  const quoteText = truncateAtWord(rawText, MAX_REVIEW_CHARS);
  const rawAuthor = hasValue(current?.name) ? current!.name! : 'Madison Grace';
  const quoteAuthor = formatReviewerName(rawAuthor);

  // The image rotates alongside the review. If there are more reviews than
  // gallery photos, we wrap so every review still has a picture. If there's
  // only one image available, every slide reuses it (no visible flicker).
  const currentSlide =
    slideImages[safeActive % slideImages.length] ?? slideImages[0] ?? { image: '', alt: '' };

  // ---- Feature-image crossfade ---------------------------------------------
  // Two-slot crossfade: both slot wrappers stay in the DOM stacked absolutely,
  // and whichever one is marked data-visible='true' is at full opacity while
  // the other is at 0. When `currentSlide` changes we write the new image
  // into the currently-idle slot and flip `topImageSlot` — CSS opacity handles
  // both halves of the swap simultaneously, so the outgoing image is still
  // visible when the incoming one starts fading in. No moment of transparency,
  // no page-background flash.
  type ImageSlide = { image: string; alt: string };
  const [imageSlotA, setImageSlotA] = useState<ImageSlide>({ image: '', alt: '' });
  const [imageSlotB, setImageSlotB] = useState<ImageSlide>({ image: '', alt: '' });
  const [topImageSlot, setTopImageSlot] = useState<'a' | 'b'>('a');
  // Guard against re-running the effect for the same image URL (e.g. property
  // data re-fetches that produce a fresh object identity for the same slide).
  const lastShownImageRef = useRef<string>('');

  useEffect(() => {
    const target = currentSlide;
    if (!target.image) return;
    if (target.image === lastShownImageRef.current) return;

    // First image ever shown — mount straight into slot A with no crossfade.
    // (The wrapper is already at data-visible='true' from initial state, so
    // the image appears at full opacity on first paint.)
    if (lastShownImageRef.current === '') {
      setImageSlotA(target);
      setTopImageSlot('a');
      lastShownImageRef.current = target.image;
      return;
    }

    // Subsequent updates: write into the idle slot and flip the top.
    const nextTop: 'a' | 'b' = topImageSlot === 'a' ? 'b' : 'a';
    if (nextTop === 'a') setImageSlotA(target);
    else setImageSlotB(target);
    setTopImageSlot(nextTop);
    lastShownImageRef.current = target.image;
  }, [currentSlide.image, currentSlide.alt, topImageSlot]);

  // ---- Quote-text crossfade -----------------------------------------------
  // Same two-slot pattern as the feature image. Both effects trigger off the
  // same `active` state change (via derived `quoteText`/`currentSlide`), so
  // React commits both slot swaps in a single render pass — the text and
  // image crossfades start on the same frame and use the same FADE_MS
  // duration, keeping them perfectly synchronized.
  type TextSlide = { quote: string; author: string };
  const [textSlotA, setTextSlotA] = useState<TextSlide>({ quote: '', author: '' });
  const [textSlotB, setTextSlotB] = useState<TextSlide>({ quote: '', author: '' });
  const [topTextSlot, setTopTextSlot] = useState<'a' | 'b'>('a');
  const lastShownQuoteRef = useRef<string>('');

  useEffect(() => {
    const target: TextSlide = { quote: quoteText, author: quoteAuthor };
    if (!target.quote) return;
    if (target.quote === lastShownQuoteRef.current) return;

    // First quote — seed slot A directly with no crossfade.
    if (lastShownQuoteRef.current === '') {
      setTextSlotA(target);
      setTopTextSlot('a');
      lastShownQuoteRef.current = target.quote;
      return;
    }

    const nextTop: 'a' | 'b' = topTextSlot === 'a' ? 'b' : 'a';
    if (nextTop === 'a') setTextSlotA(target);
    else setTextSlotB(target);
    setTopTextSlot(nextTop);
    lastShownQuoteRef.current = target.quote;
  }, [quoteText, quoteAuthor, topTextSlot]);

  // Advance to a specific slide. No swap timers or `transitioning` gates
  // needed — both the text and image crossfades are driven by React effects
  // reacting to state changes, and each crossfade smoothly re-targets to
  // wherever CSS opacity is currently at when a new one starts (so rapid
  // clicks interrupt cleanly rather than queueing up).
  const handleSelect = useCallback(
    (index: number) => {
      if (index === active) return;
      setActive(index);
    },
    [active],
  );

  // Navigation arrows. Both handlers wrap around the ends so the carousel
  // never dead-ends: prev from slide 0 jumps to the last, next from the
  // last jumps back to 0. Using `safeActive` (not raw `active`) keeps the
  // arithmetic safe if the review count shrinks under our feet.
  const handlePrev = useCallback(() => {
    if (slideCount <= 1) return;
    handleSelect((safeActive - 1 + slideCount) % slideCount);
  }, [handleSelect, safeActive, slideCount]);
  const handleNext = useCallback(() => {
    if (slideCount <= 1) return;
    handleSelect((safeActive + 1) % slideCount);
  }, [handleSelect, safeActive, slideCount]);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      aria-label="Resident review"
      className="reveal-fade-up w-full bg-background py-12 md:py-16 lg:py-20"
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="residentreview.heading"
          label="Heading"
          defaultValue="<p>What residents say</p>"
          as="h2"
          className="font-heading mb-8 text-center text-4xl font-bold leading-[1.05] text-primary sm:text-5xl md:mb-10 md:text-left"
        />

        <div className="relative">
          {/* Feature image — cycles through the property gallery in sync
              with the reviews. Two absolutely-stacked slots crossfade the
              photo: while slot A fades out, slot B fades in, so nothing is
              ever fully transparent (no page-background flash). Height is
              set on the container since both slots are position:absolute. */}
          <div className="relative w-full lg:w-[68%]">
            <EditableSlotGroup
              groupPath="residentreview.image"
              definition={featureImageDef}
              className="block"
            >
              <div className="relative h-[320px] w-full sm:h-[420px] lg:h-[560px]">
                <div
                  className="resident-review-image-slot absolute inset-0"
                  data-visible={topImageSlot === 'a' ? 'true' : 'false'}
                  aria-hidden={topImageSlot === 'a' ? undefined : 'true'}
                >
                  {imageSlotA.image ? (
                    <VibesitesImage
                      url={imageSlotA.image}
                      alt={imageSlotA.alt || 'Residents enjoying community amenities'}
                      sizes="(min-width: 1024px) 68vw, 100vw"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full bg-surface" />
                  )}
                </div>
                <div
                  className="resident-review-image-slot absolute inset-0"
                  data-visible={topImageSlot === 'b' ? 'true' : 'false'}
                  aria-hidden={topImageSlot === 'b' ? undefined : 'true'}
                >
                  {imageSlotB.image ? (
                    <VibesitesImage
                      url={imageSlotB.image}
                      alt={imageSlotB.alt || 'Residents enjoying community amenities'}
                      sizes="(min-width: 1024px) 68vw, 100vw"
                      className="h-full w-full object-cover"
                    />
                  ) : null}
                </div>
              </div>
            </EditableSlotGroup>

          </div>

          {/* Quote card — overlaps image on all sizes; inset from image
              edges on mobile so the photo stays visible around it.
              Fixed min-height so the card doesn't resize between slides. */}
          <div
            className="resident-review-card relative z-10 mx-4 -mt-20 flex flex-col justify-center bg-primary px-6 py-10 text-white min-h-[300px] sm:mx-10 sm:-mt-28 sm:min-h-[340px] sm:px-10 sm:py-12 lg:absolute lg:right-0 lg:top-1/2 lg:mx-0 lg:mt-0 lg:min-h-[420px] lg:w-[52%] lg:max-w-[640px] lg:-translate-y-1/2 lg:px-14 lg:py-16"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 64 64"
              className="resident-review-card__quote absolute left-4 top-4 h-16 w-16 opacity-30 sm:left-6 sm:top-6 sm:h-20 sm:w-20 lg:left-8 lg:top-8"
              fill="currentColor"
            >
              <path d="M18 12C10 12 4 20 4 32v20h20V32H12c0-8 4-12 10-12V12zm26 0c-8 0-14 8-14 20v20h20V32H38c0-8 4-12 10-12V12z" />
            </svg>

            {/* Quote text — two-slot crossfade to match the feature image.
                CSS Grid stacks both slots in the same cell so the taller of
                the two drives the container height and there's no jitter
                between reviews of different lengths. The card's min-height
                still holds the outer bounds so short quotes don't shrink
                the panel. */}
            <div className="relative grid">
              <div
                className="resident-review-text-slot col-start-1 row-start-1"
                data-visible={topTextSlot === 'a' ? 'true' : 'false'}
                aria-hidden={topTextSlot === 'a' ? undefined : 'true'}
              >
                <p className="font-body text-base leading-relaxed sm:text-lg md:text-xl">
                  {textSlotA.quote}
                </p>
                <p className="mt-6 hidden font-heading text-base font-bold lg:block lg:text-lg">
                  {textSlotA.author ? `– ${textSlotA.author}` : ''}
                </p>
              </div>
              <div
                className="resident-review-text-slot col-start-1 row-start-1"
                data-visible={topTextSlot === 'b' ? 'true' : 'false'}
                aria-hidden={topTextSlot === 'b' ? undefined : 'true'}
              >
                <p className="font-body text-base leading-relaxed sm:text-lg md:text-xl">
                  {textSlotB.quote}
                </p>
                <p className="mt-6 hidden font-heading text-base font-bold lg:block lg:text-lg">
                  {textSlotB.author ? `– ${textSlotB.author}` : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Prev / next arrows — sit OUTSIDE the feature image. On desktop
              the negative left/right offsets push them into the section's
              side padding zone (past the image on the left, past the card
              on the right). On mobile the image is edge-to-edge so we hug
              the container edges; vertical position is anchored to each
              breakpoint's image midpoint (image heights: 320 / 420 / 560,
              so centers are 160 / 210 / 280). z-30 keeps them above the
              blue card on the right where the two would otherwise overlap. */}
          {slideCount > 1 && (
            <>
              <button
                type="button"
                onClick={handlePrev}
                aria-label="Previous review"
                className="absolute left-1 top-[160px] z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-neutral-900/85 text-white shadow-lg transition hover:bg-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:left-2 sm:top-[210px] sm:h-12 sm:w-12 lg:-left-4 lg:top-1/2"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.25}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5 sm:h-6 sm:w-6"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={handleNext}
                aria-label="Next review"
                className="absolute right-1 top-[160px] z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-neutral-900/85 text-white shadow-lg transition hover:bg-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:right-2 sm:top-[210px] sm:h-12 sm:w-12 lg:-right-4 lg:top-1/2"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.25}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5 sm:h-6 sm:w-6"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Pagination dots */}
        {slideCount > 1 && (
          <div className="mt-8 flex items-center justify-center gap-3">
            {reviews.map((_, i) => {
              const isActive = i === safeActive;
              return (
                <button
                  key={i}
                  type="button"
                  aria-label={`Show review ${i + 1} of ${slideCount}`}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => handleSelect(i)}
                  className={`h-3 w-3 rounded-full transition-opacity ${
                    isActive
                      ? 'bg-primary opacity-100'
                      : 'resident-review-dot opacity-40 hover:opacity-70'
                  }`}
                />
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        .resident-review-card__quote {
          color: #ffffff;
        }
        .resident-review-dot {
          background-color: #d6d1c6;
        }
        /* Two-slot crossfade — shared by both the quote text and the feature
           image so they animate in perfect lockstep. Both slots stay in the
           DOM stacked in the same layout position; the one marked
           data-visible='true' is opaque, the other at 0. Because opacity
           animates on both slots simultaneously, the outgoing content is
           still visible when the incoming one begins fading in — there's no
           moment of transparency where the background could show through. */
        .resident-review-image-slot,
        .resident-review-text-slot {
          opacity: 0;
          transition: opacity ${FADE_MS}ms ease-in-out;
          will-change: opacity;
        }
        .resident-review-image-slot[data-visible='true'],
        .resident-review-text-slot[data-visible='true'] {
          opacity: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .resident-review-image-slot,
          .resident-review-text-slot {
            transition-duration: 0ms;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentReview',
  displayName: 'Resident Review',
  tokens: {
    'residentreview.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>What residents say</p>',
    },
  },
  slotGroups: {
    'residentreview.image': {
      label: 'Resident Review Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'photo_gallery[1].image',
          suggestedFields: [
            'photo_gallery[1].image',
            'photo_gallery[2].image',
            'photo_gallery[3].image',
            'photo_gallery[0].image',
            'property_featured_image',
          ],
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultField: 'photo_gallery[1].alt_text',
          defaultValue: '',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 1,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
  collectionBindings: {
    'residentreview.items': {
      label: 'Resident Reviews',
      collection: 'reviews',
      defaultLimit: 5,
      description:
        'Resident testimonials displayed in the quote carousel next to the feature image.',
    },
  },
};

(ResidentReview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ResidentReview;
